import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Infinity as InfinityIcon, Headset, Cpu, Wallet, Package, KeyRound, Activity, ShieldAlert, AlertCircle, Zap, GitBranch, MessageSquare } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import { FadeUp, Stagger, staggerItem, LineReveal } from '../components/Reveal';
import BrowserFrame from '../components/BrowserFrame';
import { BuilderCanvasMock, ConfigPanelMock, TestConsoleMock, PublishModalMock } from '../components/StudioMock';

const featureIcons = [InfinityIcon, Headset, Cpu, Wallet, Package, KeyRound];

const monitors = [
  { icon: MessageSquare, name: 'Chat Sessions' },
  { icon: Zap, name: 'Action Runs' },
  { icon: GitBranch, name: 'Orphan Actions' },
  { icon: ShieldAlert, name: 'Policy Violations' },
  { icon: AlertCircle, name: 'Error Logs' },
  { icon: Activity, name: 'Health' },
];

export default function Product() {
  const { t } = useLang();
  return (
    <div>
      <section className="bg-[#10282C] grain relative overflow-hidden pt-36 pb-24">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <p className="eyebrow text-[18px] font-semibold underline underline-offset-8 decoration-2 text-[#00C5C2]">{t('nav.product')}</p>
          <h1 className="mt-5 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-3xl">
            <LineReveal lines={[t('features.title')]} />
          </h1>
        </div>
      </section>

      <section className="py-24 bg-[#F6F8F7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 space-y-16">
          {featureIcons.map((Icon, i) => (
            <FadeUp key={i} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start border-b border-[#10282C]/8 pb-14">
              <span className="md:col-span-1 font-display text-3xl font-bold text-[#10282C]/12">0{i + 1}</span>
              <div className="md:col-span-5">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#00918F]/10 text-[#00918F]">
                  <Icon size={20} strokeWidth={1.8} />
                </span>
                <h2 className="mt-4 font-display text-xl sm:text-2xl font-semibold tracking-tight text-[#10282C]">{t(`features.f${i + 1}t`)}</h2>
              </div>
              <p className="md:col-span-6 text-base text-[#10282C]/60 leading-relaxed">{t(`features.f${i + 1}b`)}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="py-28 bg-white border-y border-[#10282C]/6">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="eyebrow text-[18px] font-semibold underline underline-offset-8 decoration-2 text-[#00918F]">Inside the Studio</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#10282C] max-w-2xl leading-snug">
              {t('hero.caption')}
            </h2>
          </FadeUp>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <FadeUp>
              <BrowserFrame url="gyeno/studio/agent/sales-coach"><BuilderCanvasMock /></BrowserFrame>
              <p className="mt-4 text-sm text-[#10282C]/55 leading-relaxed">{t('how.s2b')}</p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <BrowserFrame url="gyeno/studio/agent/config"><ConfigPanelMock /></BrowserFrame>
              <p className="mt-4 text-sm text-[#10282C]/55 leading-relaxed">{t('features.f3b')}</p>
            </FadeUp>
            <FadeUp>
              <BrowserFrame url="gyeno/studio/test-console"><TestConsoleMock /></BrowserFrame>
              <p className="mt-4 text-sm text-[#10282C]/55 leading-relaxed">{t('how.s3b')}</p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <BrowserFrame url="gyeno/studio/publish"><PublishModalMock /></BrowserFrame>
              <p className="mt-4 text-sm text-[#10282C]/55 leading-relaxed">Version-controlled, safe to iterate — every publish carries release notes and can be rolled back.</p>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="py-28 bg-[#10282C] grain relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="eyebrow text-[18px] font-semibold underline underline-offset-8 decoration-2 text-[#00C5C2]">Monitoring & Governance</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-2xl leading-snug">
              {t('manifesto.ch3t')}
            </h2>
            <p className="mt-4 text-base text-white/55 leading-relaxed max-w-xl">{t('manifesto.ch3b')}</p>
          </FadeUp>
          <Stagger className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
            {monitors.map(({ icon: Icon, name }) => (
              <motion.div key={name} variants={staggerItem} className="rounded-xl border border-white/10 bg-white/4 p-5 hover:border-[#00C5C2]/40" style={{ transition: 'border-color 0.3s ease' }}>
                <Icon size={18} className="text-[#00C5C2]" strokeWidth={1.7} />
                <p className="mt-3 text-[14px] font-semibold text-white/85">{name}</p>
              </motion.div>
            ))}
          </Stagger>
          <FadeUp className="mt-14">
            <Link to="/signup" data-testid="product-cta-signup" className="btn-coral inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[15px] font-semibold">
              {t('nav.getStarted')} <ArrowRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
